import React, { useCallback, useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import HealthChecksList from '../components/HealthChecksList';
import type { AppSanityReport, AutopilotTemplates, SanityChecksResponse } from '../types';
import { reportError } from '../utils/errors';
import './SettingsHome.lane36.css';

/** Only reports with checks have visible content on the Settings health card. */
export function visibleAppSanityReports(checks: SanityChecksResponse): AppSanityReport[] {
  return checks.apps.filter((report) => report.checks.length > 0);
}

export function hasVisibleSanityChecks(checks: SanityChecksResponse): boolean {
  return checks.global.length > 0 || visibleAppSanityReports(checks).length > 0;
}

function identityDisplayOrigin(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const url = new URL(value);
    if (url.protocol === 'https:' || url.protocol === 'http:') return url.origin;
  } catch {
    // Older API responses may contain arbitrary configured text.
  }
  return 'configured (redacted)';
}

const HIDDEN_IDENTITY_ERROR = 'Provider details are hidden. Check server logs for diagnostics.';

interface LogLevelResponse {
  config: string | null;
  env: string | null;
  current: string | null;
}

interface SetLogLevelRequest {
  level: string;
}

interface BuildInfo {
  git_hash: string;
  git_message: string;
  build_time: string;
  rust_version: string;
  build_profile: string;
  pipeline_id: string;
  gitlab_url?: string;
}

interface ExportCapability {
  can_export: boolean;
  scope: string;
}

interface CliManifestArtifact {
  target: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  download_url: string;
  minimum_compatible_backend: string;
}

interface CliManifest {
  cli_name: string;
  version: string;
  build: BuildInfo;
  artifacts: CliManifestArtifact[];
}

interface YmlIdTestResponse {
  ok: boolean;
  details: Record<string, unknown>;
  error?: string | null;
}

interface YmlIdSettingsResponse {
  configured: boolean;
  enabled: boolean;
  issuer?: string | null;
  base_url?: string | null;
  auth_mode?: string | null;
  last_test_at?: string | null;
  last_test_status?: string | null;
  last_test_error?: string | null;
}

interface LogCleanupRunStatus {
  running: boolean;
  started_at?: string | null;
  finished_at?: string | null;
  retention_days: number;
  batch_size: number;
  max_batches: number;
  batches_completed: number;
  deleted_rows: number;
  last_error?: string | null;
}

interface LogCleanupSettingsResponse {
  retention_days: number;
  batch_size: number;
  scheduled_max_batches_per_pass: number;
  status: LogCleanupRunStatus;
}

export default function SettingsHome({ apiClient }: { apiClient: ApiClient }) {
  const [checks, setChecks] = useState<SanityChecksResponse>({ global: [], apps: [] });
  const [checksError, setChecksError] = useState<string | null>(null);
  const [loadingChecks, setLoadingChecks] = useState(false);

  // yml-id (Identity Provider) state
  const [ymlIdSettings, setYmlIdSettings] = useState<YmlIdSettingsResponse | null>(null);
  const [ymlIdTesting, setYmlIdTesting] = useState(false);
  const [ymlIdTestResult, setYmlIdTestResult] = useState<YmlIdTestResponse | null>(null);
  const [ymlIdError, setYmlIdError] = useState<string | null>(null);

  // Log level state
  const [logLevel, setLogLevel] = useState<string | null>(null);
  const [logEnvLevel, setLogEnvLevel] = useState<string | null>(null);
  const [logLevelError, setLogLevelError] = useState<string | null>(null);
  const [savingLogLevel, setSavingLogLevel] = useState(false);
  const [logLevelSuccess, setLogLevelSuccess] = useState<string | null>(null);
  const [logCleanup, setLogCleanup] = useState<LogCleanupSettingsResponse | null>(null);
  const [logCleanupBusy, setLogCleanupBusy] = useState(false);
  const [logCleanupError, setLogCleanupError] = useState<string | null>(null);

  // Build info state
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  const [buildInfoError, setBuildInfoError] = useState<string | null>(null);
  const [cliManifest, setCliManifest] = useState<CliManifest | null>(null);
  const [cliManifestLoading, setCliManifestLoading] = useState(false);
  const [cliManifestError, setCliManifestError] = useState<string | null>(null);
  const [autopilotTemplates, setAutopilotTemplates] = useState<AutopilotTemplates | null>(null);
  const [autopilotBusy, setAutopilotBusy] = useState(false);
  const [autopilotError, setAutopilotError] = useState<string | null>(null);
  const [autopilotSuccess, setAutopilotSuccess] = useState<string | null>(null);

  // Export capability state
  const [exportCapability, setExportCapability] = useState<ExportCapability | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoadingChecks(true);
    apiClient
      .get<SanityChecksResponse>('/api/settings/sanity')
      .then((data) => {
        if (mounted) {
          setChecks(data);
          setChecksError(null);
        }
      })
      .catch((err) => {
        if (mounted) {
          setChecksError(err.message ?? 'Failed to load sanity checks');
        }
      })
      .finally(() => {
        if (mounted) {
          setLoadingChecks(false);
        }
      });

    // Load log level
    apiClient
      .get<LogLevelResponse>('/api/settings/log-level')
      .then((data) => {
        if (mounted) {
          setLogLevel(data.current ?? data.config ?? null);
          setLogEnvLevel(data.env ?? null);
        }
      })
      .catch((err) => {
        console.error('Failed to load log level:', err);
      });

    apiClient
      .get<LogCleanupSettingsResponse>('/api/settings/log-cleanup')
      .then((data) => {
        if (mounted) {
          setLogCleanup(data);
          setLogCleanupError(null);
        }
      })
      .catch((err) => {
        if (mounted) {
          setLogCleanupError(err instanceof Error ? err.message : 'Failed to load log cleanup settings');
        }
      });

    // Load build info
    apiClient
      .get<BuildInfo>('/api/settings/build-info')
      .then((data) => {
        if (mounted) {
          setBuildInfo(data);
          setBuildInfoError(null);
        }
      })
      .catch((err) => {
        if (mounted) {
          // Don't show error for build info - it's nice-to-have, not essential
          console.error('Failed to load build info:', err);
        }
      });

    apiClient
      .get<AutopilotTemplates>('/api/settings/autopilot')
      .then((data) => {
        if (mounted) {
          setAutopilotTemplates(data);
        }
      })
      .catch((err) => {
        if (mounted) {
          setAutopilotError(err instanceof Error ? err.message : 'Failed to load autopilot templates');
        }
      });

    // Load export capability (admin-only)
    apiClient
      .get<ExportCapability>('/api/admin/conversations/export/capability')
      .then((data) => {
        if (mounted) {
          setExportCapability(data);
        }
      })
      .catch(() => {
        // Not authorized - that's fine, just don't show export
        if (mounted) {
          setExportCapability({ can_export: false, scope: 'conversations:export' });
        }
      });

    // Load yml-id settings
    apiClient
      .get<YmlIdSettingsResponse>('/api/settings/yml-id')
      .then((data) => {
        if (mounted) {
          setYmlIdSettings(data);
        }
      })
      .catch(() => {
        // Not available - fine
      });

    return () => {
      mounted = false;
    };
  }, [apiClient]);

  const handleLogLevelChange = async (level: string) => {
    setLogLevelError(null);
    setLogLevelSuccess(null);
    setSavingLogLevel(true);
    try {
      await apiClient.post<{ level: string; message: string }>(
        '/api/settings/log-level',
        { level }
      );
      setLogLevel(level);
      setLogLevelSuccess(`Log level changed to ${level}`);
      // Clear success message after 3 seconds
      setTimeout(() => setLogLevelSuccess(null), 3000);
    } catch (err) {
      setLogLevelError(err instanceof Error ? err.message : 'Failed to set log level');
    } finally {
      setSavingLogLevel(false);
    }
  };

  const loadLogCleanup = useCallback(async () => {
    const data = await apiClient.get<LogCleanupSettingsResponse>('/api/settings/log-cleanup');
    setLogCleanup(data);
    setLogCleanupError(null);
    return data;
  }, [apiClient]);

  const handleStartLogCleanup = async () => {
    setLogCleanupBusy(true);
    setLogCleanupError(null);
    try {
      const status = await apiClient.post<LogCleanupRunStatus>('/api/settings/log-cleanup/run', {});
      setLogCleanup((prev) =>
        prev
          ? { ...prev, status }
          : {
              retention_days: status.retention_days,
              batch_size: status.batch_size,
              scheduled_max_batches_per_pass: 10,
              status
            }
      );
    } catch (err) {
      setLogCleanupError(err instanceof Error ? err.message : 'Failed to start log cleanup');
    } finally {
      setLogCleanupBusy(false);
    }
  };

  useEffect(() => {
    if (!logCleanup?.status.running) {
      return;
    }
    const timer = window.setInterval(() => {
      loadLogCleanup().catch((err) => {
        setLogCleanupError(err instanceof Error ? err.message : 'Failed to refresh log cleanup status');
      });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [logCleanup?.status.running, loadLogCleanup]);

  const saveAutopilotTemplates = async () => {
    if (!autopilotTemplates) {
      return;
    }
    setAutopilotBusy(true);
    setAutopilotError(null);
    setAutopilotSuccess(null);
    try {
      const updated = await apiClient.post<AutopilotTemplates>(
        '/api/settings/autopilot',
        autopilotTemplates
      );
      setAutopilotTemplates(updated);
      setAutopilotSuccess('Autopilot templates updated.');
      setTimeout(() => setAutopilotSuccess(null), 3000);
    } catch (err) {
      setAutopilotError(err instanceof Error ? err.message : 'Failed to update autopilot templates');
    } finally {
      setAutopilotBusy(false);
    }
  };

  const handleTestYmlId = async () => {
    setYmlIdTesting(true);
    setYmlIdError(null);
    setYmlIdTestResult(null);
    try {
      const result = await apiClient.post<YmlIdTestResponse>('/api/settings/yml-id/test', {});
      setYmlIdTestResult(result);
      const updated = await apiClient.get<YmlIdSettingsResponse>('/api/settings/yml-id');
      setYmlIdSettings(updated);
    } catch {
      setYmlIdError('Identity provider connection test failed');
    } finally {
      setYmlIdTesting(false);
    }
  };

  const handleLoadCliManifest = async () => {
    setCliManifestLoading(true);
    setCliManifestError(null);
    try {
      const data = await apiClient.get<CliManifest>('/api/cli/manifest');
      setCliManifest(data);
    } catch (err) {
      setCliManifestError(err instanceof Error ? err.message : 'Failed to load CLI manifest');
    } finally {
      setCliManifestLoading(false);
    }
  };

  const handleExportConversations = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const response = await fetch('/api/admin/conversations/export', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.headers.get('Content-Disposition')
        ?.split('filename=')[1]?.replace(/"/g, '') || 'conversations_export.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const linuxCliArtifact = cliManifest?.artifacts.find(
    (artifact) => artifact.target === 'linux-x86_64'
  ) ?? cliManifest?.artifacts[0] ?? null;
  const cliInstallCommand = `curl -fsSL ${window.location.origin}/api/cli/install.sh | bash`;

  const formatBytes = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes < 0) {
      return 'unknown size';
    }
    if (bytes < 1024 * 1024) {
      return `${Math.ceil(bytes / 1024)} KiB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  };

  const appHealthReports = visibleAppSanityReports(checks);

  return (
    <section className="settings-home-36">
      <header className="settings-home-36__intro">
        <p className="settings-home-36__eyebrow">Environment configuration</p>
        <h2>Settings</h2>
        <p>Configure tools and access, manage runtime settings, and review environment health.</p>
        <nav className="settings-home-36__navigation" aria-label="Settings destinations">
          <Link to="/settings/models" className="settings-home-36__destination">
            <span className="settings-home-36__destination-title">Tool Settings <span aria-hidden="true">→</span></span>
            <span className="settings-home-36__destination-description">Configure agent tools and models.</span>
          </Link>
          <Link to="/settings/ui-validation" className="settings-home-36__destination">
            <span className="settings-home-36__destination-title">UI Validation <span aria-hidden="true">→</span></span>
            <span className="settings-home-36__destination-description">Configure browser validation.</span>
          </Link>
          <Link to="/executors" className="settings-home-36__destination">
            <span className="settings-home-36__destination-title">Executors <span aria-hidden="true">→</span></span>
            <span className="settings-home-36__destination-description">Manage execution hosts and workers.</span>
          </Link>
          <Link to="/settings/tokens" className="settings-home-36__destination">
            <span className="settings-home-36__destination-title">API Tokens <span aria-hidden="true">→</span></span>
            <span className="settings-home-36__destination-description">Manage tokens for API access.</span>
          </Link>
        </nav>
      </header>
      <section className="settings-home-36__group" aria-labelledby="settings-home-36-logging">
        <header className="settings-home-36__section-heading">
          <h2 id="settings-home-36-logging">Logging & retention</h2>
          <p>Control log detail and clean up historical records.</p>
        </header>
        <div className="settings-home-36__cards">
          <div className="card">
            <div className="card-header">
              <h3>Log Level</h3>
            </div>
            {logLevelError && <ErrorBanner message={logLevelError} onDismiss={() => setLogLevelError(null)} />}
            {logLevelSuccess && (
              <div className="status" style={{ color: 'var(--color-success-text)', marginBottom: 'var(--space-md)' }}>
                {logLevelSuccess}
              </div>
            )}
            <div className="form-row">
              <label htmlFor="log-level-select">Current level:</label>
              <select
                id="log-level-select"
                value={logLevel ?? 'info'}
                onChange={(e) => handleLogLevelChange(e.target.value)}
                disabled={savingLogLevel}
              >
                <option value="trace">Trace</option>
                <option value="debug">Debug</option>
                <option value="info">Info</option>
                <option value="warn">Warning</option>
                <option value="error">Error</option>
              </select>
              {savingLogLevel && <span className="spinner-inline"></span>}
            </div>
            {logEnvLevel && (
              <p className="status">
                Environment (RUST_LOG): <span className="mono">{logEnvLevel}</span>
              </p>
            )}
            <p className="hint">
              Changes apply immediately without restart. Debug and trace levels may produce large logs.
            </p>
          </div>
          <div className="card">
            <div className="card-header">
              <h3>Log Cleanup</h3>
              <button
                type="button"
                onClick={handleStartLogCleanup}
                disabled={logCleanupBusy || Boolean(logCleanup?.status.running)}
              >
                {logCleanup?.status.running ? 'Running...' : logCleanupBusy ? 'Starting...' : 'Run cleanup'}
              </button>
            </div>
            {logCleanupError && (
              <ErrorBanner message={logCleanupError} onDismiss={() => setLogCleanupError(null)} />
            )}
            {!logCleanup ? (
              <p className="status">Loading log cleanup settings...</p>
            ) : (
              <>
                <dl className="key-value-list">
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Retention</dt>
                    <dd className="key-value-list__value">{logCleanup.retention_days} days</dd>
                  </div>
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Batch size</dt>
                    <dd className="key-value-list__value">{logCleanup.batch_size.toLocaleString()} rows</dd>
                  </div>
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Scheduled pass</dt>
                    <dd className="key-value-list__value">
                      up to {logCleanup.scheduled_max_batches_per_pass.toLocaleString()} batches
                    </dd>
                  </div>
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Manual status</dt>
                    <dd className="key-value-list__value">
                      <span className={logCleanup.status.running ? 'status-text--disabled' : 'status-text--ok'}>
                        {logCleanup.status.running ? 'running' : 'idle'}
                      </span>
                    </dd>
                  </div>
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Deleted</dt>
                    <dd className="key-value-list__value">
                      {logCleanup.status.deleted_rows.toLocaleString()} rows across{' '}
                      {logCleanup.status.batches_completed.toLocaleString()} batches
                    </dd>
                  </div>
                  {logCleanup.status.started_at && (
                    <div className="key-value-list__row">
                      <dt className="key-value-list__key">Started</dt>
                      <dd className="key-value-list__value">
                        <span className="mono">{logCleanup.status.started_at}</span>
                      </dd>
                    </div>
                  )}
                  {logCleanup.status.finished_at && (
                    <div className="key-value-list__row">
                      <dt className="key-value-list__key">Finished</dt>
                      <dd className="key-value-list__value">
                        <span className="mono">{logCleanup.status.finished_at}</span>
                      </dd>
                    </div>
                  )}
                  {logCleanup.status.last_error && (
                    <div className="key-value-list__row">
                      <dt className="key-value-list__key">Error</dt>
                      <dd className="key-value-list__value">
                        <span className="mono">{logCleanup.status.last_error}</span>
                      </dd>
                    </div>
                  )}
                </dl>
                <div className="actions">
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => void loadLogCleanup()}
                    disabled={logCleanupBusy}
                  >
                    Refresh
                  </button>
                </div>
                <p className="hint">
                  Cleanup runs in short database transactions and is also scheduled automatically by the janitor.
                </p>
              </>
            )}
          </div>
        </div>
      </section>
      <section className="settings-home-36__group" aria-labelledby="settings-home-36-runtime">
        <header className="settings-home-36__section-heading">
          <h2 id="settings-home-36-runtime">Runtime & local tools</h2>
          <p>Inspect this build and download the matching CLI.</p>
        </header>
        <div className="settings-home-36__cards">
          {buildInfo && (
            <div className="card">
              <div className="card-header">
                <h3>Build Information</h3>
              </div>
              <dl className="key-value-list">
                {buildInfo.gitlab_url && (
                  <>
                    <div className="key-value-list__row">
                      <dt className="key-value-list__key">Pipeline</dt>
                      <dd className="key-value-list__value">
                        {buildInfo.pipeline_id !== 'unknown' ? (
                          <a
                            href={`${buildInfo.gitlab_url}/-/pipelines/${buildInfo.pipeline_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link"
                          >
                            <code className="mono">#{buildInfo.pipeline_id}</code>
                          </a>
                        ) : (
                          <span className="mono">unknown</span>
                        )}
                      </dd>
                    </div>
                    <div className="key-value-list__row">
                      <dt className="key-value-list__key">Commit</dt>
                      <dd className="key-value-list__value">
                        {buildInfo.git_hash !== 'unknown' ? (
                          <a
                            href={`${buildInfo.gitlab_url}/-/commit/${buildInfo.git_hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link"
                          >
                            <code className="mono">{buildInfo.git_hash}</code>
                          </a>
                        ) : (
                          <code className="mono">{buildInfo.git_hash}</code>
                        )}
                      </dd>
                    </div>
                  </>
                )}
                {!buildInfo.gitlab_url && (
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Commit</dt>
                    <dd className="key-value-list__value">
                      <code className="mono">{buildInfo.git_hash}</code>
                    </dd>
                  </div>
                )}
                {buildInfo.git_message && (
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Message</dt>
                    <dd className="key-value-list__value">{buildInfo.git_message}</dd>
                  </div>
                )}
                <div className="key-value-list__row">
                  <dt className="key-value-list__key">Build time</dt>
                  <dd className="key-value-list__value">{buildInfo.build_time}</dd>
                </div>
                <div className="key-value-list__row">
                  <dt className="key-value-list__key">Rust version</dt>
                  <dd className="key-value-list__value">{buildInfo.rust_version}</dd>
                </div>
                <div className="key-value-list__row">
                  <dt className="key-value-list__key">Profile</dt>
                  <dd className="key-value-list__value">
                    <span className={buildInfo.build_profile === 'release' ? 'status-text--ok' : 'status-text--disabled'}>
                      {buildInfo.build_profile}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          )}
          <div className="card">
            <div className="card-header">
              <h3>Local CLI</h3>
              <button type="button" onClick={handleLoadCliManifest} disabled={cliManifestLoading}>
                {cliManifestLoading ? 'Loading…' : 'Load CLI download details'}
              </button>
            </div>
            <p className="status">
              Download the matching runner CLI. Use free serverless mode for local-only agent starts,
              or managed mode for backend sessions, AI Fence gateway keys, and browser continuation.
            </p>
            <pre className="command-snippet">
              <code>{cliInstallCommand}</code>
            </pre>
            <p className="hint">Loading download details may archive the current runner release and begin its wait to become the stable CLI.</p>
            {cliManifestError && (
              <ErrorBanner message={cliManifestError} onDismiss={() => setCliManifestError(null)} />
            )}
            {cliManifestLoading && <p className="status">Loading CLI manifest…</p>}
            {linuxCliArtifact && (
              <>
                <dl className="key-value-list">
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Binary</dt>
                    <dd className="key-value-list__value">
                      <code className="mono">{linuxCliArtifact.filename}</code>
                    </dd>
                  </div>
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Version</dt>
                    <dd className="key-value-list__value">
                      <code className="mono">{cliManifest?.version ?? 'unknown'}</code>
                    </dd>
                  </div>
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Size</dt>
                    <dd className="key-value-list__value">{formatBytes(linuxCliArtifact.size_bytes)}</dd>
                  </div>
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">SHA-256</dt>
                    <dd className="key-value-list__value">
                      <code className="mono">{linuxCliArtifact.sha256}</code>
                    </dd>
                  </div>
                </dl>
                <div className="actions">
                  <a
                    className="link-button"
                    href={linuxCliArtifact.download_url}
                    download={linuxCliArtifact.filename}
                  >
                    Download Linux CLI
                  </a>
                  <a
                    className="link-button secondary"
                    href="/api/cli/install.sh"
                    target="_blank"
                    rel="noreferrer"
                  >
                    View install script
              </a>
              <a
                className="link-button secondary"
                href="/docs/mission-control-cli-quickstart.html"
                target="_blank"
                rel="noreferrer"
              >
                    Open CLI quickstart
                  </a>
                </div>
                <p className="hint">
                  Free mode: run <code>serverless-cli</code> for local testing. Managed/contact-us mode:
                  launch with a bootstrap token from a conversation or workspace session.
                </p>
              </>
            )}
          </div>
        </div>
      </section>
      <section className="settings-home-36__group" aria-labelledby="settings-home-36-access">
        <header className="settings-home-36__section-heading">
          <h2 id="settings-home-36-access">Identity, automation & data</h2>
          <p>Review identity integration, edit agent templates, and access available exports.</p>
        </header>
        <div className="settings-home-36__cards">
          <div className="card">
            <div className="card-header">
              <h3>Identity Provider</h3>
              <button type="button" onClick={handleTestYmlId} disabled={ymlIdTesting || !ymlIdSettings?.configured}>
                {ymlIdTesting ? 'Testing...' : 'Test Admin Connection'}
              </button>
            </div>
            <p className="hint">This test requests an OAuth token, checks identity provider access, and saves the result. It runs only when you select the button.</p>
            {ymlIdError && <ErrorBanner message={ymlIdError} onDismiss={() => setYmlIdError(null)} />}
            {!ymlIdSettings && (
              <p className="status">Loading identity settings…</p>
            )}
            {ymlIdSettings && !ymlIdSettings.configured && (
              <p className="status">Not configured — add a <code className="mono">[yml_id]</code> section to server config.</p>
            )}
            {ymlIdSettings && ymlIdSettings.configured && (
              <dl className="key-value-list">
                <div className="key-value-list__row">
                  <dt className="key-value-list__key">Issuer origin</dt>
                  <dd className="key-value-list__value"><span className="mono">{identityDisplayOrigin(ymlIdSettings.issuer)}</span></dd>
                </div>
                <div className="key-value-list__row">
                  <dt className="key-value-list__key">Base origin</dt>
                  <dd className="key-value-list__value"><span className="mono">{identityDisplayOrigin(ymlIdSettings.base_url)}</span></dd>
                </div>
                <div className="key-value-list__row">
                  <dt className="key-value-list__key">Auth mode</dt>
                  <dd className="key-value-list__value">{ymlIdSettings.auth_mode ?? '—'}</dd>
                </div>
                {ymlIdSettings.last_test_at && (
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Last test</dt>
                    <dd className="key-value-list__value">
                      <span className={ymlIdSettings.last_test_status === 'ok' ? 'status-text--ok' : 'status-text--missing'}>
                        {ymlIdSettings.last_test_status ?? '—'}
                      </span>
                      {' '}<span className="mono">{ymlIdSettings.last_test_at}</span>
                    </dd>
                  </div>
                )}
                {ymlIdSettings.last_test_error && (
                  <div className="key-value-list__row">
                    <dt className="key-value-list__key">Error</dt>
                    <dd className="key-value-list__value"><span className="mono">{HIDDEN_IDENTITY_ERROR}</span></dd>
                  </div>
                )}
              </dl>
            )}
            {ymlIdTestResult && (
              <div className="status" style={{ color: ymlIdTestResult.ok ? 'var(--color-success-text)' : 'var(--color-error-text)', marginTop: 'var(--space-sm)' }}>
                {ymlIdTestResult.ok
                  ? 'Admin connection OK — token acquired, all endpoints accessible'
                  : `Failed: ${HIDDEN_IDENTITY_ERROR}`}
              </div>
            )}
          </div>
          <div className="card">
            <div className="card-header">
              <h3>Autopilot Templates</h3>
              <button type="button" onClick={saveAutopilotTemplates} disabled={autopilotBusy || !autopilotTemplates}>
                {autopilotBusy ? 'Saving...' : 'Save templates'}
              </button>
            </div>
            {autopilotError && <ErrorBanner message={autopilotError} onDismiss={() => setAutopilotError(null)} />}
            {autopilotSuccess && <p className="status">{autopilotSuccess}</p>}
            {!autopilotTemplates ? (
              <p className="status">Loading templates…</p>
            ) : (
              <div className="stack">
                <label className="stack">
                  <span>Build failed</span>
                  <textarea
                    rows={3}
                    value={autopilotTemplates.build_failed}
                    onChange={(event) =>
                      setAutopilotTemplates((prev) =>
                        prev ? { ...prev, build_failed: event.target.value } : prev
                      )
                    }
                  />
                </label>
                <label className="stack">
                  <span>Merge conflict</span>
                  <textarea
                    rows={3}
                    value={autopilotTemplates.merge_conflict}
                    onChange={(event) =>
                      setAutopilotTemplates((prev) =>
                        prev ? { ...prev, merge_conflict: event.target.value } : prev
                      )
                    }
                  />
                </label>
                <label className="stack">
                  <span>Ready (semi)</span>
                  <textarea
                    rows={2}
                    value={autopilotTemplates.ready_semi}
                    onChange={(event) =>
                      setAutopilotTemplates((prev) =>
                        prev ? { ...prev, ready_semi: event.target.value } : prev
                      )
                    }
                  />
                </label>
                <label className="stack">
                  <span>Ready (full)</span>
                  <textarea
                    rows={2}
                    value={autopilotTemplates.ready_full}
                    onChange={(event) =>
                      setAutopilotTemplates((prev) =>
                        prev ? { ...prev, ready_full: event.target.value } : prev
                      )
                    }
                  />
                </label>
                <p className="status">
                  Placeholders: {'{{app_name}}'}, {'{{app_slug}}'}, {'{{source_branch}}'}, {'{{target_branch}}'},
                  {' {{mr_iid}}'}, {'{{mr_url}}'}, {'{{pipeline_id}}'}, {'{{pipeline_status}}'}, {'{{pipeline_url}}'},
                  {' {{conversation_id}}'}, {'{{conversation_url}}'}, {'{{autopilot_mode}}'}
                </p>
              </div>
            )}
          </div>
          {exportCapability?.can_export && (
            <div className="card">
              <div className="card-header">
                <h3>Data Export</h3>
              </div>
              {exportError && <ErrorBanner message={exportError} onDismiss={() => setExportError(null)} />}
              <p className="status">
                Export all conversations including prompts and agent outputs as a zip file.
              </p>
              <div className="actions">
                <button
                  type="button"
                  onClick={handleExportConversations}
                  disabled={exporting}
                >
                  {exporting ? 'Exporting...' : 'Export Conversations'}
                </button>
              </div>
              <p className="hint">
                The export includes all conversation data: messages, prompts sent to agents, and raw agent outputs.
                Use the API endpoint with an API key bearing the <code>conversations:export</code> scope for programmatic access.
              </p>
            </div>
          )}
        </div>
      </section>
      <section className="settings-home-36__group" aria-labelledby="settings-home-36-health">
        <header className="settings-home-36__section-heading">
          <h2 id="settings-home-36-health">Environment health</h2>
          <p>Review instance-wide checks and application configuration. Model gateway discovery is read-only; gateway key handoff is not tested.</p>
        </header>
        <div className="card full">
          <div className="card-header">
            <h3>Admin sanity checks</h3>
            <button onClick={() => window.location.reload()} disabled={loadingChecks}>
              Refresh
            </button>
          </div>
          {checksError && <ErrorBanner message={checksError} onDismiss={() => setChecksError(null)} />}
          {loadingChecks && <p className="status">Checking configuration…</p>}
          {!loadingChecks && !hasVisibleSanityChecks(checks) && (
            <p className="status">No checks to display.</p>
          )}
          {checks.global.length > 0 && <HealthChecksList checks={checks.global} />}
          {appHealthReports.map((report) => (
            <div className="stack" key={report.app_id}>
              <h4>
                <Link to={`/apps/${report.app_id}`}>Application {report.app_id.slice(0, 8)}</Link>
              </h4>
              <HealthChecksList checks={report.checks} />
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}
